import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/templates/app-shell'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { Button } from '@/components/ui/button'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import {
  associationKindOptions,
  usePlatformAssociationFilters,
} from './usePlatformAssociationFilters'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import { platformAdminNav } from '@/pages/platformAdminNav'
import type { PlatformSurface } from '@/pages/PlatformAdminSignInPage'

import {
  PlatformConnectionSummary,
  type ConnectionSummary,
} from '@/pages/PlatformConnectionSummary'

export type AssociationStatus = 'active' | 'trial' | 'suspended'

export interface PlatformAssociation {
  demo?: boolean
  demo_profile?: 'fab' | null
  seed_status?: 'seeding' | 'seeded' | 'failed' | null
  seeded_at?: string | null
  seed_failed_at?: string | null
  seeded_by?: string | null
  seeded_row_count?: number | null
  id: number
  name: string
  web_address_name: string
  web_address?: string
  status: AssociationStatus
  seat_count: number
  accounting_connection_status: 'connected' | 'disconnected' | 'not_connected'
  open_accounting_error_count: number
  email_send_count: number
  connection_summary: ConnectionSummary
}

export interface PlatformAssociationsResponse {
  associations: PlatformAssociation[]
}

interface AssociationResponse {
  association: PlatformAssociation
}

interface PlatformAssociationsListPageProps {
  platform: PlatformSurface
  unauthorizedFallback?: React.ReactNode
  onCreateAssociation?: () => void
}

export const statusLabel: Record<AssociationStatus, string> = {
  active: 'Active',
  trial: 'Trial',
  suspended: 'Suspended',
}

export const statusVariant: Record<AssociationStatus, BadgeProps['variant']> = {
  active: 'success',
  trial: 'warning',
  suspended: 'destructive',
}

const associationColumns = [
  {
    id: 'name',
    header: 'Name',
    cell: (association) => (
      <span className="inline-flex items-center gap-2">
        {association.name}
        {association.demo && <Badge variant="neutral">Demo</Badge>}
      </span>
    ),
  },
  {
    id: 'web_address',
    header: 'Subdomain',
    cell: (association) => association.web_address ?? association.web_address_name,
    className: 'text-muted-foreground',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (association) => (
      <Badge variant={statusVariant[association.status]}>{statusLabel[association.status]}</Badge>
    ),
  },
  {
    id: 'connection_summary',
    header: 'Connections',
    cell: (association) => <PlatformConnectionSummary summary={association.connection_summary} />,
  },
  {
    id: 'accounting_connection_status',
    header: 'Accounting',
    cell: (association) => (
      <Badge
        variant={
          association.accounting_connection_status === 'connected'
            ? 'success'
            : association.accounting_connection_status === 'disconnected'
              ? 'warning'
              : 'neutral'
        }
      >
        {association.accounting_connection_status === 'not_connected'
          ? 'Not connected'
          : association.accounting_connection_status === 'connected'
            ? 'Connected'
            : 'Disconnected'}
      </Badge>
    ),
  },
  {
    id: 'open_accounting_error_count',
    header: 'Sync errors',
    headerClassName: 'whitespace-nowrap',
    cell: (association) => association.open_accounting_error_count,
    className: 'text-muted-foreground',
  },
  {
    id: 'seat_count',
    header: 'Seats',
    cell: (association) => association.seat_count,
    className: 'text-muted-foreground',
  },
  {
    id: 'email_send_count',
    header: 'Emails sent',
    headerClassName: 'whitespace-nowrap',
    cell: (association) => association.email_send_count,
    className: 'text-muted-foreground',
  },
] satisfies ListPageColumn<PlatformAssociation>[]

export default function PlatformAssociationsListPage({
  platform,
  unauthorizedFallback,
  onCreateAssociation,
}: PlatformAssociationsListPageProps) {
  const navigate = useNavigate()
  const filters = usePlatformAssociationFilters()
  const queryClient = useQueryClient()
  const associationsQuery = useQuery({
    queryKey: ['platform-associations', filters.path],
    queryFn: () => getJSON<PlatformAssociationsResponse>(filters.path),
    retry: false,
  })
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: AssociationStatus }) =>
      patchJSON<AssociationResponse>(`/api/v1/platform/associations/${id}`, { status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform-associations'] })
    },
  })

  if (
    associationsQuery.error instanceof ApiError &&
    associationsQuery.error.status === 401 &&
    unauthorizedFallback
  ) {
    return unauthorizedFallback
  }

  return (
    <AppShell
      associationName={platform.brand}
      nav={platformAdminNav}
      user={{ name: 'Platform Admin' }}
    >
      <ListPage
        title="Associations"
        primaryAction={{ label: 'New association', onClick: onCreateAssociation }}
        search={{
          value: filters.search,
          onChange: filters.changeSearch,
          placeholder: 'Search associations...',
        }}
        toolbar={
          <FilterDropdown
            label="Kind"
            emptyLabel="All kinds"
            options={associationKindOptions}
            selected={filters.selected}
            onChange={filters.changeKinds}
          />
        }
        table={{
          rows: associationsQuery.data?.associations ?? [],
          columns: associationColumns,
          getRowId: (association) => association.id,
          getRowLabel: (association) => association.name,
          onRowOpen: (association) => navigate(`/platform/associations/${association.id}`),
          state: associationsQuery.isLoading
            ? 'loading'
            : associationsQuery.isError
              ? 'error'
              : 'ready',
          loadingTitle: 'Loading Associations',
          emptyTitle: filters.active ? 'No matching Associations' : 'No Associations yet',
          emptyAction: filters.active ? (
            <Button variant="outline" onClick={filters.clear}>
              Clear filters
            </Button>
          ) : undefined,
          errorTitle: 'Associations unavailable',
          errorDescription: 'Refresh and try again.',
          actions: (association) => {
            const suspended = association.status === 'suspended'

            return [
              {
                label: suspended ? 'Restore' : 'Suspend',
                disabled: updateStatus.isPending && updateStatus.variables?.id === association.id,
                onSelect: () =>
                  updateStatus.mutate({
                    id: association.id,
                    status: suspended ? 'active' : 'suspended',
                  }),
              },
            ]
          },
        }}
      />
    </AppShell>
  )
}
